import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

// Page transition variants
export const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.6, 0.01, 0.05, 0.95],
      when: 'beforeChildren',
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
};

// Stagger children animation
export const staggerContainer = {
  initial: {},
  enter: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

// Fade up animation for children
export const fadeUpVariant = {
  initial: {
    opacity: 0,
    y: 30,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.6, 0.01, 0.05, 0.95],
    },
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.3,
    },
  },
};

// Scale fade animation
export const scaleFadeVariant = {
  initial: {
    opacity: 0,
    scale: 0.95,
  },
  enter: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.3,
    },
  },
};

// Image dissolve/scatter effect
export const dissolveVariant = {
  initial: {
    opacity: 1,
    filter: 'blur(0px)',
  },
  exit: {
    opacity: 0,
    filter: 'blur(10px)',
    transition: {
      duration: 0.6,
      ease: 'easeInOut',
    },
  },
};

// Text scatter animation (letter by letter)
export const textScatterVariant = {
  initial: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: 0.02,
      staggerDirection: -1,
    },
  },
};

export const letterScatterVariant = {
  initial: {
    opacity: 1,
    y: 0,
    rotateX: 0,
  },
  exit: {
    opacity: 0,
    y: -20,
    rotateX: 90,
    transition: {
      duration: 0.4,
      ease: 'easeInOut',
    },
  },
};

// Hover scale animation
export const hoverScale = {
  scale: 1.02,
  transition: { duration: 0.3, ease: 'easeOut' },
};

// Card hover animation
export const cardHoverVariant = {
  rest: {
    scale: 1,
    boxShadow: '0 0 0 rgba(0, 240, 255, 0)',
  },
  hover: {
    scale: 1.02,
    boxShadow: '0 0 30px rgba(0, 240, 255, 0.15)',
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

// Image swap animation for product cards
export const imageSwapVariant = {
  initial: { opacity: 1 },
  hover: { opacity: 0 },
};

export const secondImageVariant = {
  initial: { opacity: 0 },
  hover: { opacity: 1 },
};

// Page transition wrapper component
export const PageTransition = ({ children }) => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="enter"
        exit="exit"
        variants={pageVariants}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Animated text component that scatters on exit
export const ScatterText = ({ children, className = '' }) => {
  const letters = children.split('');
  
  return (
    <motion.span 
      className={`inline-flex ${className}`}
      variants={textScatterVariant}
      initial="initial"
      exit="exit"
    >
      {letters.map((letter, index) => (
        <motion.span
          key={index}
          variants={letterScatterVariant}
          style={{ display: 'inline-block' }}
        >
          {letter === ' ' ? '\u00A0' : letter}
        </motion.span>
      ))}
    </motion.span>
  );
};

// Smooth scroll to section
export const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

// Parallax scroll hook values
export const useParallax = (value, distance) => {
  return value * distance;
};

export default PageTransition;
